import DriverTabs, {
  RouteParamsType,
} from '../components/driverOrders/driverTabs';
import React, {useCallback, useEffect, useState} from 'react';
import {getClientType, getStoreName} from '@buncha/common/types/common';
import {useIsFocused, useRoute} from '@react-navigation/native';

import {Analytics} from '../../../src/config/analytics/analytics';
import {BackHeaderLeft} from '@buncha/common/components/BackHeaderLeft';
import {Box} from '../../../src/components/core/view';
import {Buyer} from '../types/common';
import {ChatModes} from '@buncha/batchDetails/types/Chat';
import ChatOpener from '@buncha/components/composites/ChatOpener';
import {DriverScreens} from '../navigation/type';
import Header from '../../../src/components/composites/Header/Header';
import {HomeScreens} from '../../../src/home/navigation/types';
import If from '@buncha/components/conditional/If';
import {MainScreens} from '@buncha/navigation/types';
import MapOverviewIcon from '../../../src/components/core/icon/MapOverviewIcon';
import {Modules} from '@buncha/utils/types';
import {NativeStackScreenProps} from '@react-navigation/native-stack';
import ScreenHeader from '../../common/components/screenHeader';
import {StyleSheet} from 'react-native';
import {Touchable} from '../../../src/components/core/touchable';
import {UserPhoneNumber} from '../types/driverOrders';
import {UserTypes} from '@buncha/onBoarding/types';
import {isValidLatLong} from '../shared/util';
import {isWeb} from '@buncha/utils/common';
import {navigation} from '../../../src/config/navigation';
import onfleetService from '@buncha/config/onfleet';
import {useAppSelector} from '@buncha/config/redux/hooks';
import {useColorThemeType} from '../../../src/appStyles/hooks/theme';
import {useGetLocale} from '@buncha/localization/hooks';
import {useIsMeijerStore} from '@buncha/home/hooks/home';

type Props = NativeStackScreenProps<any, any>;

const DriverOrders: React.FC<Props> = () => {
  const theme = useColorThemeType('main');
  const isFocus = useIsFocused();
  const {title} = useGetLocale(Modules.driver, HomeScreens.Driver) ?? {};
  const driverTasks = useAppSelector(gState => gState.home.driverTasks);

  const [usersData, setUsersData] = useState<UserPhoneNumber[]>([]);

  const route = useRoute();
  const routeParams: RouteParamsType | undefined = route?.params;
  const runId = Number(routeParams?.runId) || 0;
  const storeId = routeParams?.storeId ?? 0;
  const isMeijerStore = useIsMeijerStore(storeId);

  const navigateToMapOverview = useCallback(() => {
    navigation.navigate(MainScreens.Driver, {
      screen: DriverScreens.MapOverview,
      params: {
        coords:
          driverTasks
            ?.filter?.(task =>
              isValidLatLong(
                task.destinationAddress?.locationLatitude,
                task.destinationAddress?.locationLongitude,
              ),
            )
            ?.map(task => ({
              lat: task.destinationAddress?.locationLatitude,
              lon: task.destinationAddress?.locationLongitude,
              buyerName: task.recipientName,
            })) ?? [],
      },
    });
    Analytics.eventWithProps('Navigate to map overview page', {
      type: 'CTA',
      userType: 'driver',
      client: getClientType(isMeijerStore),
      storeType: getStoreName(String(storeId)),
    });
  }, [driverTasks, isMeijerStore, storeId]);

  const globalState = useAppSelector(gState => gState.home);
  const shiftId = globalState.batchShiftId;
  const shiftKey = globalState.batchShiftKey;

  const goBack = useCallback(() => {
    if (routeParams?.backPage === HomeScreens.YourSchedule)
      return navigation.goBack();
    navigation.navigate(MainScreens.Home, {
      screen: HomeScreens.AboutShift,
      params: {
        shiftId: shiftId,
        shiftKey: shiftKey,
      },
    });
  }, [routeParams?.backPage, shiftId, shiftKey]);

  const updateUsersData = useCallback((users: Buyer[]) => {
    const data: UserPhoneNumber[] = users.map((user: Buyer) => ({
      phoneNumber: user.phoneNumber,
      user: user.id,
    }));
    setUsersData(s => [...s, ...data]);
  }, []);

  useEffect(() => {
    onfleetService?.startLocationTracking();

    return () => {
      onfleetService?.stopLocationTracking();
    };
  }, []);

  return (
    <Header
      headerRightComponent={
        !isWeb() && (
          <Box style={styles.headerRightComponent}>
            <If condition={runId && !isMeijerStore}>
              <ChatOpener
                usersData={usersData}
                hideRole={UserTypes.shopper}
                mode={ChatModes.run}
                runId={isFocus ? runId : undefined}
                style={styles.chatOpener}
              />
            </If>
            <Touchable onPress={navigateToMapOverview} style={styles.touchable}>
              <MapOverviewIcon
                fill={theme?.icon?.blue?.[300]}
                viewBox=" 0 -3 20 20"
              />
            </Touchable>
          </Box>
        )
      }
      headerColor={theme?.background?.blue?.[300]}
      headerLeftComponent={<BackHeaderLeft onClick={goBack} />}>
      <ScreenHeader title={title?.tasks} />
      <Box style={styles.parentBox}>
        <DriverTabs updateUsersData={updateUsersData} />
      </Box>
    </Header>
  );
};

export default DriverOrders;

const styles = StyleSheet.create({
  parentBox: {
    paddingHorizontal: 20,
    flex: 1,
  },
  webHeader: {
    padding: 30,
  },
  touchable: {
    marginRight: 20,
  },
  chatOpener: {
    marginRight: 10,
    marginTop: 10,
  },
  headerRightComponent: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'center',
    height: 50,
  },
});
